'use client';

import { c as _c } from "react/compiler-runtime";
import { useI18n } from '~/features/i18n';
import { useShouldDisplayPrices, VATKindDisplay } from '~/features/portals/money';
import { useMemo } from 'react';
import { useHoverState } from '~/modules/browser/DOM';
import { useDeviceType } from '~/components/primitives/hooks/useDeviceType';
import Link from 'next/link';
import { usePortalCtx } from '~/features/global/portal';
import { usePathname, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { cx } from 'class-variance-authority';
import Title from '~/components/utils/Title';
import { ActivityCardFacts, ActivityCardRoute, resolveCardDescription } from './utils';
import { Unit_RenderEUR } from '~/modules/units/components';
import { Units } from '~/modules/units';
import { resolveCardPrice } from '~/features/activities/search/shared';
import { captureScrollForModalRestoration } from '~/modules/browser/scroll';
import { HISTORY, HREF } from '~/modules/browser/history';
import Icon from '~/components/primitives/Icon';
import { DEFAULT_DISPLAY_PARTICIPANTS } from '@funkey/constants';
import { I18N_CONF } from '@funkey/locales/config';
import useWishlistState from '~/features/wishlist/useWishlistState';
import { usePostHog } from 'posthog-js/react';
import { buildActivityHref, type ActivityCardProps } from './index';

/** Sponsored badge with lightning bolt icon */
const SponsoredBadge: React.FC = () => {
  const $ = _c(4);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n) {
    t0 = i18n._("feature.activities.sponsoredBadge", undefined, {
      message: "Sponsored"
    });
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="shrink-0">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};

/** The reworked PLP card: review score, an always-visible description and VAT
 * basis, the facts a visitor needs to shortlist without opening the activity,
 * and a total for the group size the results were priced at. */
export const ActivityCard_Finetuning: React.FC<ActivityCardProps> = props => {
  const i18n = useI18n();
  const portal = usePortalCtx();
  const shouldDisplayPrices = useShouldDisplayPrices();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Only show the route icons that match the selected filter (selecting "partner
  // comes to me" hides the "I go to the partner" car, and vice versa). No filter → all.
  const routeFilter = props.routeFilter ?? [];
  const showGoToIcon = props.canGoTo && (routeFilter.length === 0 || routeFilter.includes('FIXED'));
  const showComesToIcon = props.comesToUs && (routeFilter.length === 0 || routeFilter.includes('CUSTOM'));
  // The partner's fixed location is further than the searched radius: the activity
  // is only in the results because the partner also comes to you. Drop the km figure
  // from the car pill (it's "out of the question" to travel there) but keep the icon —
  // and explain it in the tooltip.
  const goToIsTooFar = showGoToIcon && props.fixedDistanceKm != null && props.searchRadiusKm != null && props.fixedDistanceKm > props.searchRadiusKm;

  // The group size `price` was calculated for. Comes from the same payload as
  // the price itself, so the card's total can never disagree with the
  // "Prijzen berekend voor X deelnemers" line above the results.
  const pageParticipants = props.pricedParticipants ?? DEFAULT_DISPLAY_PARTICIPANTS;
  // What this activity is actually priced for. The page asks every card for one
  // size; an activity that cannot take it is quoted at the nearest size it can,
  // so the total underneath names that size rather than the page's.
  const displayParticipants = props.priceForParticipants ?? pageParticipants;
  const description = resolveCardDescription(props);
  const cardPrice = resolveCardPrice({
    price: props.price,
    priceIsForDisplaySize: props.priceIsForDisplaySize,
    hasMultipleVariants: props.hasMultipleVariants,
    participants: displayParticipants,
    isSubstitutedSize: displayParticipants !== pageParticipants
  });
  const toggleWishlist = () => {
    const willAdd = !isInWishlist;
    wishlist.toggle({
      activityId: Number(props.id),
      name: props.name,
      slug: props.slug,
      imagePath: props.mediaPath ?? null,
      variantId: null,
      languageId: null,
      addedLanguage: i18n.language
    });
    posthog.capture(willAdd ? 'wishlist.item.added' : 'wishlist.item.removed', {
      activity_id: Number(props.id),
      activity_name: props.name
    });
  };
  const IMG_ASPECT_RATIO = {
    w: 365,
    h: 220
  };
  const [, hoverProps] = useHoverState();
  const wishlist = useWishlistState();
  const posthog = usePostHog();
  const isInWishlist = wishlist.isInWishlist(Number(props.id));
  const {
    isDesktop
  } = useDeviceType();
  const shouldEnableHover = isDesktop;
  const shouldOpenInNewTab = props.openIn === 'new-tab';
  const shouldOpenInModal = props.openIn === 'modal';
  const hoverInteractionProps = shouldEnableHover ? hoverProps : {
    onMouseOver() {},
    onMouseLeave() {}
  };
  const activityHref = useMemo(() => buildActivityHref({
    portal,
    slug: props.slug,
    id: props.id,
    isPrimary: props.isPrimary,
    boostedAdID: props.boostedAdID,
    locale: i18n.locale,
    selectedParticipants: props.selectedParticipants
  }), [portal.portal, portal.baseURLPath, props.slug, props.id, props.isPrimary, props.boostedAdID, i18n.locale, props.selectedParticipants]);

  // Build the final href — append modal=1 for modal mode, or returnUrl for new tab
  const finalHref = useMemo(() => {
    if (shouldOpenInModal) {
      const separator = activityHref.includes('?') ? '&' : '?';
      // The intercepted modal is the mobile route, and it was the only one not carrying
      // `returnUrl`. The PDP's location widget reads the active /teambuildings location
      // filter out of that param, so on mobile it opened with no city while the same
      // card on desktop opened with one. Built from the router rather than
      // `window.location` so it is right on the server render too.
      const query = searchParams.toString();
      const returnUrl = encodeURIComponent(query ? `${pathname}?${query}` : pathname);
      return `${activityHref}${separator}modal=1&returnUrl=${returnUrl}`;
    }
    return activityHref;
  }, [activityHref, shouldOpenInModal, pathname, searchParams]);

  // Build href with returnUrl at click time (window.location is mutable, so not in useMemo)
  function getHrefWithReturnUrl() {
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (!currentUrl) return finalHref;
    const separator_0 = finalHref.includes('?') ? '&' : '?';
    return `${finalHref}${separator_0}returnUrl=${encodeURIComponent(currentUrl)}`;
  }
  const Wrapper = useMemo<React.FC<typeof hoverProps & {
    className: string;
    children: React.ReactNode;
    onClick?(): void;
  }>>(() => wrapperProps => props.disableHref ? <article {...wrapperProps} /> : <article className="contents">
                    <Link {...wrapperProps} href={finalHref} scroll={shouldOpenInModal ? false : undefined} target={shouldOpenInNewTab ? '_blank' : undefined} rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined} onClick={e => {
      // For new tab: open manually with returnUrl, prevent default Link
      if (shouldOpenInNewTab) {
        e.preventDefault();
        window.open(getHrefWithReturnUrl(), '_blank', 'noopener,noreferrer');
      }
      // For non-modal, non-newtab: force hard navigation to avoid
      // the @modal intercept route triggering
      else if (!shouldOpenInModal) {
        e.preventDefault();
        // Set backToOverview before navigating
        const activityPath = I18N_CONF.getPageSlugForLocale('activities', i18n.locale);
        const isOnPDP = pathname.includes(`/${activityPath}/`);
        if (!isOnPDP) {
          HISTORY.specialPages.setValue(prev => ({
            ...prev,
            'PDP.backToOverview': {
              ...HREF.parseURL(window.location),
              portal: portal.portal
            }
          }));
        }
        wrapperProps.onClick?.();
        window.location.href = finalHref;
        return;
      }
      captureScrollForModalRestoration(pathname);

      // Only set backToOverview if we're NOT already on a PDP page
      const activityPath_0 = I18N_CONF.getPageSlugForLocale('activities', i18n.locale);
      const isOnPDP_0 = pathname.includes(`/${activityPath_0}/`);
      if (!isOnPDP_0) {
        const backToOverviewData = {
          ...HREF.parseURL(window.location),
          portal: portal.portal
        };
        HISTORY.specialPages.setValue(prev_0 => ({
          ...prev_0,
          'PDP.backToOverview': backToOverviewData
        }));
      }
      wrapperProps.onClick?.();
    }}>
                        {wrapperProps.children}
                    </Link>
                </article>, [props.disableHref, shouldOpenInNewTab, finalHref, shouldOpenInModal, pathname]);
  return <Wrapper {...hoverInteractionProps} onClick={props.onClick} data-testid="activity-card" data-sponsored={!!props.boostedAdID || undefined} className={cx('@container shadow-xs @min-[250px]:p-3 group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white p-2 transition',
  // shouldEnableHover && 'hover:shadow-md',
  // Sponsored ad styling - subtle amber gradient ring (debug only)
  props.debugShowSponsoredStyling && props.boostedAdID && 'ring-2 ring-amber-400/50 ring-offset-1', props.className)}>
            <header className={cx('relative w-full shrink-0 overflow-hidden rounded-2xl bg-blue-100', props.inactive && 'opacity-60 grayscale')} style={{
      aspectRatio: `${IMG_ASPECT_RATIO.w}/${IMG_ASPECT_RATIO.h}`
    }}>
                {props.adminScore ? <div data-testid="activity-card-admin-score" className="absolute bottom-2 right-2 z-10 flex flex-col gap-0.5 rounded-lg bg-blue-900/85 px-2 py-1 font-mono text-[10px] leading-tight text-white backdrop-blur-sm">
                        <span>rank {props.adminScore.rankScore.toFixed(3)}</span>
                        <span>sim {props.adminScore.similarity.toFixed(3)}</span>
                        <span>pop {props.adminScore.popularityBoost.toFixed(3)}</span>
                    </div> : null}
                <Image src={props.mediaPath} alt={`${props.name} - Header`} className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" width={IMG_ASPECT_RATIO.w} height={IMG_ASPECT_RATIO.h} />

                {/* The corner is the span's, not the button's: `hit-area-*` sets
                    `position: relative`, which beats an `absolute` on the same
                    element, and the heart ended up laid out under the image and
                    clipped away by the header's `overflow-hidden`. */}
                {!props.hideWishlist && <span className="absolute right-3 top-3 z-2">
                        <button type="button" data-testid="activity-card-wishlist" aria-pressed={isInWishlist} aria-label={isInWishlist ? i18n._('wishlist.remove', undefined, {
          message: 'Remove from wishlist'
        }) : i18n._('wishlist.add', undefined, {
          message: 'Add to wishlist'
        })} className={cx('hit-area-4 md:hit-area-2 shadow-2xs flex rounded-full p-2 backdrop-blur-md transition hover:scale-105',
        // Dark glass over the photo while it is only an
        // offer; once saved it turns solid so the state
        // reads at a glance rather than by the fill.
        isInWishlist ? 'bg-white text-red-500' : 'bg-blue-900/40 text-white hover:bg-blue-900/55')} onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          toggleWishlist();
        }}>
                            <Icon name="heart" size="xs" fill={isInWishlist ? 'currentColor' : 'none'} />
                        </button>
                    </span>}

                {/* One star and the score rather than five stars: at this size a
                    row of five reads as decoration, and the number is what a
                    visitor compares between cards. */}
                {props.reviews && <p data-testid="activity-card-review-score" className="shadow-2xs absolute left-3 top-3 z-1 inline-flex items-center gap-1 rounded-full bg-blue-900/40 py-1 pl-2 pr-2.5 text-xs font-extrabold leading-none text-white backdrop-blur-md">
                        <Icon name="star" className="size-2.5 fill-current text-yellow" aria-hidden />
                        {new Intl.NumberFormat(i18n.locale, {
          maximumFractionDigits: 1
        }).format(props.reviews.average)}
                        <span className="sr-only">
                            {i18n._('activity.card.reviewCount', {
            count: props.reviews.count
          })}
                        </span>
                    </p>}

                {/* Bottom gradient with title — always visible */}
                <div className="absolute inset-0 flex flex-col justify-end px-5 pb-5 pt-10" style={{
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 35%, rgba(0, 0, 0, 0.45) 70%, rgba(0, 0, 0, 0.8) 100%)'
      }}>
                    <Title as="h2" size="lg" className="text text-left [text-shadow:2px_2px_4px_rgba(0,0,0,0.3)]" color="light">
                        {props.name}
                    </Title>
                </div>
            </header>

            {/* The top padding clears the image's rounded corner, so the box only looks
                centred when the bottom is trimmed to match what is left of it. */}
            <div className="-mt-6 rounded-b-2xl bg-black/[0.03] px-3 pb-2.5 pt-9">
                <ActivityCardRoute band comesToUs={showComesToIcon} canGoTo={showGoToIcon && !goToIsTooFar} fixedDistanceKm={props.fixedDistanceKm} />
            </div>

            {/* Text lines up with the image edge: the card padding is the only inset. */}
            <main className="mt-4 flex h-full flex-col gap-3 px-3 pb-3">
                <ActivityCardFacts minParticipants={props.minParticipants} maxParticipants={props.maxParticipants} durationMinutes={props.durationMinutes} locationType={props.locationType} comesToUs={showComesToIcon}
      // Beyond the searched radius the partner's own location is not
      // an option the visitor asked for: the activity is listed only
      // because the partner travels. Claiming "go to the partner"
      // there would assert the one thing that is not on offer.
      canGoTo={showGoToIcon && !goToIsTooFar} fixedDistanceKm={props.fixedDistanceKm} hideRoute />

                {description && <p className="line-clamp-2 text-sm leading-relaxed text-blue-500">
                        {description}
                    </p>}

                {shouldDisplayPrices && <div className="mt-auto flex flex-col gap-1 pt-1">
                        <p className="flex flex-wrap items-baseline gap-x-2">
                            {/* The figure is the cheapest variant, so on an activity
                                with several it is where the price starts, not what
                                the group pays. */}
                            {cardPrice.kind === 'amount' && cardPrice.isFrom && <span data-testid="activity-card-price-from" className="text-sm font-medium text-blue-400">
                                    {i18n._('activity.card.priceFrom', undefined, {
              message: 'from'
            })}
                                </span>}
                            <span className="leading-none! text-2xl font-bold text-primary" data-testid="activity-card-price">
                                {cardPrice.kind === 'amount' ? <Unit_RenderEUR unit="EUR" value={cardPrice.perPerson} /> : i18n._('price_on_demand')}
                            </span>
                            {cardPrice.kind === 'amount' && <span data-testid="activity-card-vat-label" className="text-sm font-medium text-blue-400">
                                    {i18n._('activity.card.perPerson', undefined, {
              message: 'p.p.'
            })}{' '}
                                    <VATKindDisplay hideParentheses />
                                    {props.boostedAdID && <>
                                            {' · '}
                                            <SponsoredBadge />
                                        </>}
                                </span>}
                        </p>

                        {cardPrice.kind === 'amount' && <p className="text-sm text-gray-400" data-testid="activity-card-total-price">
                                {i18n._('activity.card.totalForParticipants', {
            count: displayParticipants,
            total: Units.formatEUR(cardPrice.total * 100, i18n.locale)
          })}
                            </p>}
                    </div>}
            </main>

            {props.children}
        </Wrapper>;
};